/**
 * OptInPopup — попап-опитування free-тарифу (Owner 2026-07-09, Крок 2).
 *
 * Два стани:
 *  1) offer      — «Безкоштовний доступ»: пояснюємо free-тариф + 2 кнопки
 *                  «Отримувати вакансії» (opt-in) / «Оформити підписку».
 *  2) confirmed  — після opt-in: 🔥 «Free версію активовано!».
 *
 * Показ (поки що ТІЛЬКИ адмінам — тест власника) керується ззовні (App.tsx).
 * Копірайт — 1:1 як затвердив власник. Стиль/структура — як LockedSectionPopup
 * (portal, центр, TgEmoji-анімовані емодзі, кнопки з popupButtons).
 */

import { createPortal } from "react-dom";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TgEmoji } from "@/components/TgEmoji";
import { popupActionsCol, popupBtnPrimary, popupBtnSecondary } from "@/lib/popupButtons";
import { PopupCloseButton } from "@/components/PopupClose";
import { useFreeOptIn } from "@/api/hooks";

export function OptInPopup({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const optIn = useFreeOptIn();
  const [confirmed, setConfirmed] = useState(false);

  // Лочимо body scroll поки попап відкритий (як в інших попапах).
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const handleOptIn = () => {
    optIn.mutate(undefined, {
      onSuccess: () => setConfirmed(true),
      // Навіть при помилці показуємо підтвердження (флаг ідемпотентний,
      // /me підтягне стан). Не блокуємо юзера технічною помилкою.
      onError: () => setConfirmed(true),
    });
  };

  const shell = (children: React.ReactNode, maxW = "max-w-sm") => (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        height:
          typeof window !== "undefined" &&
          (window as any).Telegram?.WebApp?.viewportStableHeight
            ? `${(window as any).Telegram.WebApp.viewportStableHeight}px`
            : "100vh",
      }}
    >
      <div
        className={`relative w-full ${maxW} bg-surface rounded-3xl p-5 shadow-2xl overflow-y-auto`}
        style={{ maxHeight: "calc(100% - 32px)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <PopupCloseButton onClose={onClose} />
        {children}
      </div>
    </div>
  );

  // --- Стан 2: підтвердження (вужче — контенту мало) ---
  if (confirmed) {
    return createPortal(
      shell(
        <div className="text-center py-1">
          <div className="mb-2 flex justify-center">
            <TgEmoji char="🎉" size={44} />
          </div>
          <div className="font-display text-[1.125rem] font-bold text-ink leading-tight">
            Free версію активовано!
          </div>
          <div className="text-[0.78125rem] text-ink-dim mt-2 leading-relaxed [text-wrap:balance]">
            Тепер ти отримуватимеш вакансії у своїх сферах. Сфери можна змінити
            в профілі.
          </div>
        </div>,
        "max-w-xs",
      ),
      document.body,
    );
  }

  // --- Стан 1: опитування (offer) ---
  return createPortal(
    shell(
      <>
        <div className="text-center mb-4">
          <div className="mb-2 flex justify-center">
            <TgEmoji char="📨" size={46} />
          </div>
          <div className="font-display text-[1.1875rem] font-bold text-ink leading-tight">
            Безкоштовний доступ
          </div>
          <div className="text-[0.8125rem] text-ink-dim mt-2 leading-relaxed [text-wrap:balance]">
            Отримуй digital-вакансії з усіх телеграм чатів, каналів і з усього
            тредс, виключно по своїх сферах — безкоштовно.
          </div>
        </div>

        <div className="rounded-2xl bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 p-3.5 mb-4">
          <div className="font-display text-[0.6875rem] font-bold text-ink mb-2 uppercase tracking-wide">
            Що входить:
          </div>
          <ul className="space-y-1.5 text-[0.78125rem] text-ink/85">
            <li className="flex items-start gap-2 leading-snug">
              <span className="flex w-4 shrink-0 justify-center text-primary">—</span>
              <span className="flex-1">
                Сповіщення про нові вакансії із затримкою{" "}
                4{"\u00A0"}год. для free користувачів
              </span>
            </li>
            <li className="flex items-start gap-2 leading-snug">
              <span className="flex w-4 shrink-0 justify-center text-primary">—</span>
              <span className="flex-1">
                Можливість відкрити 5 контактів щотижня
              </span>
            </li>
          </ul>
        </div>

        <div className="text-[0.75rem] text-ink-dim leading-snug text-center mb-4 [text-wrap:balance]">
          Або оформи підписку — де все миттєво та без обмежень.
        </div>

        <div className={popupActionsCol}>
          <button
            type="button"
            onClick={handleOptIn}
            disabled={optIn.isPending}
            className={popupBtnPrimary + " disabled:opacity-60"}
          >
            {optIn.isPending ? "Зачекай…" : "Отримувати вакансії"}
          </button>
          <button
            type="button"
            onClick={() => {
              onClose();
              navigate("/subscription");
            }}
            className={popupBtnSecondary}
          >
            Оформити підписку
          </button>
        </div>
      </>,
    ),
    document.body,
  );
}
